/**
 * Canister Client for ASD System - Sprint 31 Update
 * TypeScript implementation with proper type safety
 *
 * Dual-mode communication:
 * - Local development: HTTP proxy on port 5001
 * - Mainnet: @dfinity/agent direct calls
 */

import { Result,
    CandidResult,
    Device,
    DeviceInventory,
    DiscoveryHubResponse,
    DeviceApplicationRelationship,
    CredentialType,
    StorageRequest,
    StorageResponse,
    DeviceActivityLog,
    ActivityLogListResponse,
    ActivityLogStatistics,
    AddActivityLogRequest,
    KBStatistics,
    KBDocument,
    SearchRequest,
    StoreDocumentRequest,
    DocumentType,
    VersionHistory
} from './types/candid-types';

// Sprint 31: Environment-aware configuration
import {
    environment,
    isMainnet
} from './config/environment';

import {
    getCanisterIdOrDefault,
    CanisterName
} from './config/canister-ids';

import {
    initializeAdapter,
    CommunicationAdapter
} from './api/communication';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Canister ID mapping for all canisters in the system
 */
interface CanisterIds {
    aws_discovery_integration: string;
    device_manager: string;
    vector_db: string;
    credential_manager: string;
    blockchain_storage: string;
    user_manager: string;
    ii_agent: string;
    service_mapper: string;
    knowledge_base: string;
    claude_proxy: string;
    llm_proxy: string;
    ada: string;  // Sprint 28: Chat History
}

/**
 * JIT Credential data structure
 */
interface JitCredential {
    id: string;
    name: string;
    credential_type: CredentialType;
    target: string;
    username: string;
    password: string;
    permissions: string[];
    created_at: bigint;
    expires_at: bigint;
    status: string;
}

/**
 * Device tuple format [device_id, metadata]
 */
type DeviceTuple = [string, Record<string, string>];

/**
 * Application tuple format [app_id, metadata]
 */
type ApplicationTuple = [string, Record<string, string>];

/**
 * Discovery result item
 */
interface DiscoveryResult {
    device_id: string;
    status: string;
    protocol: string;
    os?: string;
    vendor?: string;
}

/**
 * Discovery batch result
 */
interface DiscoveryBatchResult {
    ip: string;
    status: 'discovered' | 'failed';
    inventory?: DeviceInventory;
    error?: string;
}

/**
 * Direct hub discovery result
 */
interface DirectHubResult {
    success: boolean;
    data?: unknown;
    error?: string;
    source: string;
}

/**
 * Timezone Configuration from Timezone Configuration Service
 * Following SOA principles - mirrors service interface from vector_db
 */
export interface TimezoneConfig {
    utc_offset_hours: number;
    label: string;
    description: string;
}

/**
 * Questionnaire component entry
 */
interface QuestionnaireComponent {
    component_name: string;
    question: string;
    answer: string;
    score: number[];  // Candid opt
}

/**
 * Questionnaire response record
 */
interface QuestionnaireResponse {
    id: string;
    service_id: string;
    section: string;
    components: QuestionnaireComponent[];
    submitted_by: string;
    submitted_at: bigint;
    notes: string[];  // Candid opt
}

/**
 * Service document type variant
 */
type ServiceDocumentType =
    | { Architecture: null }
    | { Runbook: null }
    | { SLA: null }
    | { DR: null }
    | { Other: null };

/**
 * Service document record
 */
interface ServiceDocument {
    id: string;
    service_id: string;
    document_type: ServiceDocumentType;
    filename: string;
    content_type: string;
    content: number[];  // Candid opt blob
    uploaded_by: string;
    uploaded_at: bigint;
}

/**
 * Interview session record
 */
interface InterviewSession {
    id: string;
    service_id: string;
    participant_name: string;
    participant_role: string;
    session_type: string;
    sections: string[];
    status: string;
    scheduled_at: bigint[];  // Candid opt
    started_at: bigint[];    // Candid opt
    completed_at: bigint[];  // Candid opt
    notes: string[];         // Candid opt
}

/**
 * Log callback function type for batch operations
 */
type LogCallback = (message: string, type: 'progress' | 'success' | 'detail' | 'error') => void;

/**
 * Claude Chat Message
 */
export interface ChatMessage {
    role: string;
    content: string;
}

/**
 * Claude Chat Request
 */
export interface ChatRequest {
    messages: ChatMessage[];
    context?: string;
    conversation_id?: string;
}

/**
 * Claude Chat Response
 */
export interface ChatResponse {
    message: string;
    conversation_id: string;
    tokens_used: number;
    cost_estimate: number;
}

/**
 * Claude Usage Statistics
 */
export interface UsageStats {
    total_queries: number;
    total_tokens: number;
    total_cost_usd: number;
    avg_cost_per_query: number;
}

/**
 * Sprint 31: Frontend-Assisted Request for ICP Mainnet
 * Frontend calls Claude directly, then passes response here for tracking
 */
export interface FrontendAssistedRequest {
    messages: ChatMessage[];
    context: string[];  // Candid opt text = [value] for Some, [] for None
    conversation_id: string[];  // Candid opt text = [value] for Some, [] for None
    claude_response: string;
    input_tokens: number;
    output_tokens: number;
}

/**
 * LLM Proxy Chat Request
 */
export interface LLMChatRequest {
    messages: ChatMessage[];
    context?: string;
    conversation_id?: string;
    preferred_backend?: string; // "small", "large", or "auto"
}

/**
 * LLM Proxy Chat Response
 */
export interface LLMChatResponse {
    message: string;
    conversation_id: string;
    tokens_used: number;
    backend_used: string;
    response_time_ms: number;
}

/**
 * LLM Endpoint Configuration
 */
export interface LLMEndpoint {
    name: string;
    url: string;
    model_name: string;
    backend_type: string; // "small", "large", "local"
    enabled: boolean;
    max_tokens: number;
    timeout_seconds: number;
}

/**
 * LLM Proxy Usage Statistics
 */
export interface LLMUsageStats {
    total_queries: number;
    total_tokens: number;
    small_llm_queries: number;
    large_llm_queries: number;
    local_queries: number;
}

// ============================================================================
// Enterprise Architecture Type Definitions
// ============================================================================

export interface EAOrganization {
    org_id: string;
    division: string;
    department: string;
    team: string;
    headcount: number;
    location_type: string;
    reports_to: string;
    key_responsibilities: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EAApplicationEntity {
    app_id: string;
    name: string;
    vendor: string;
    app_type: string;
    domain: string;
    description: string;
    users: number;
    criticality: string;
    integration_method: string;
    integrates_with: string[];
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EATechnicalService {
    service_id: string;
    name: string;
    application: string;
    service_type: string;
    api_interface: string;
    description: string;
    availability_sla: string;
    owner: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EABusinessServiceEntity {
    service_id: string;
    service_domain: string;
    business_service: string;
    service_offering: string;
    channel: string;
    sla: string;
    service_owner: string;
    technical_service: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EADataObject {
    data_object_id: string;
    name: string;
    domain: string;
    description: string;
    source_system: string;
    owner: string;
    classification: string;
    retention: string;
    key_attributes: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EAHRRole {
    role_id: string;
    division: string;
    department: string;
    role: string;
    grade: string;
    headcount: number;
    location: string;
    key_skills: string;
    systems_used: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EARelationship {
    relationship_id: string;
    source_type: string;
    source_id: string;
    target_type: string;
    target_id: string;
    relationship_type: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
}

// Sprint 45: New EA Entity Types

export interface EACapability {
    capability_id: string;
    domain: string;
    capability_l1: string;
    capability_l2: string;
    description: string;
    owner: string;
    key_systems: string;
    maturity_level: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EAProcess {
    process_id: string;
    process_domain: string;
    process_name: string;
    trigger: string;
    steps: string;
    owner: string;
    sla: string;
    volume_per_day: number;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EAProcessConnector {
    connector_id: string;
    process_id: string;
    step_number: number;
    step_name: string;
    swim_lane: string;
    location: string;
    action: string;
    system: string;
    integration: string;
    decision_point: string;
    sla: string;
    handoff_to: string;
    linked_app_id: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EACompliance {
    obligation_id: string;
    regulation: string;
    regulator: string;
    category: string;
    status: string;
    executive: string;
    in_scope_apps: string[];
    linked_controls: string[];
    linked_risk_categories: string[];
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

export interface EAIntegration {
    integration_id: string;
    source_system: string;
    target_system: string;
    integration_type: string;
    frequency: string;
    data_exchanged: string;
    metadata: Array<[string, string]>;
    created_at: bigint;
    updated_at: bigint;
}

/** Candid opt encoding: [] = None, [value] = Some(value) */
type CandidOpt<T> = [] | [T];

export interface EAOrganizationRequest {
    org_id: string;
    division: string;
    department: string;
    team: string;
    headcount: number;
    location_type: string;
    reports_to: string;
    key_responsibilities: string;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface EAApplicationRequest {
    app_id: string;
    name: string;
    vendor: string;
    app_type: string;
    domain: string;
    description: string;
    users: number;
    criticality: string;
    integration_method: string;
    integrates_with: CandidOpt<string[]>;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface EATechnicalServiceRequest {
    service_id: string;
    name: string;
    application: string;
    service_type: string;
    api_interface: string;
    description: string;
    availability_sla: string;
    owner: string;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface EABusinessServiceRequest {
    service_id: string;
    service_domain: string;
    business_service: string;
    service_offering: string;
    channel: string;
    sla: string;
    service_owner: string;
    technical_service: string;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface EADataObjectRequest {
    data_object_id: string;
    name: string;
    domain: string;
    description: string;
    source_system: string;
    owner: string;
    classification: string;
    retention: string;
    key_attributes: string;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface EAHRRoleRequest {
    role_id: string;
    division: string;
    department: string;
    role: string;
    grade: string;
    headcount: number;
    location: string;
    key_skills: string;
    systems_used: string;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface EARelationshipRequest {
    source_type: string;
    source_id: string;
    target_type: string;
    target_id: string;
    relationship_type: string;
    relationship_id: CandidOpt<string>;
    metadata: CandidOpt<Array<[string, string]>>;
}

export interface BulkEAImport {
    organizations: EAOrganizationRequest[];
    applications: EAApplicationRequest[];
    technical_services: EATechnicalServiceRequest[];
    business_services: EABusinessServiceRequest[];
    data_objects: EADataObjectRequest[];
    hr_roles: EAHRRoleRequest[];
    relationships: EARelationshipRequest[];
}

export interface BulkEAImportResult {
    success: boolean;
    organizations_created: number;
    applications_created: number;
    technical_services_created: number;
    business_services_created: number;
    data_objects_created: number;
    hr_roles_created: number;
    relationships_created: number;
    errors: string[];
    message: string;
}

export interface EAStatistics {
    total_organizations: number;
    total_applications: number;
    total_technical_services: number;
    total_business_services: number;
    total_data_objects: number;
    total_hr_roles: number;
    total_relationships: number;
    total_capabilities: number;
    total_processes: number;
    total_process_connectors: number;
    total_compliance: number;
    total_integrations: number;
    total_headcount: number;
    organizations_by_division: Array<[string, number]>;
    applications_by_domain: Array<[string, number]>;
    applications_by_criticality: Array<[string, number]>;
    capabilities_by_domain: Array<[string, number]>;
}

// ============================================================================
// CanisterClient Public Interface
// ============================================================================

/**
 * Public interface for CanisterClient.
 * Consumers (controllers, services) should depend on this interface,
 * not the concrete class, for better testability and decoupling.
 */
export interface CanisterClientInterface {
    // Generic canister call
    callCanister<T = unknown>(canisterName: string, methodName: string, args?: unknown[]): Promise<T>;

    // Discovery
    collectDeviceInventory(deviceId: string): Promise<Result<DeviceInventory, string>>;
    collectDeviceInventoryByIP(ipAddress: string): Promise<Result<DeviceInventory, string>>;
    discoverViaDirectHub(ipAddress: string): Promise<DirectHubResult>;
    discoverRangeViaDirectHub(ipList: string[], logCallback?: LogCallback | null): Promise<DirectHubResult>;

    // Vector DB — Devices
    getAllDevices(): Promise<{ ok: DeviceTuple[] } | { err: string }>;
    getDeviceByIp(ip: string): Promise<DeviceTuple[] | null>;
    addDeviceEmbedding(deviceId: string, metadata: Array<[string, string]>): Promise<Result<string, string>>;
    storeDiscoveredDevice(request: StorageRequest): Promise<CandidResult<StorageResponse>>;
    getDevicesByType(deviceType: string): Promise<Result<DeviceTuple[], string>>;
    searchSimilar(query: Record<string, string>, limit: number): Promise<Result<DeviceTuple[], string>>;

    // Vector DB — Applications & Relationships
    getAllApplications(): Promise<{ ok: ApplicationTuple[] } | { err: string }>;
    getAllRelationships(): Promise<{ ok: DeviceApplicationRelationship[] } | { err: string }>;

    // Vector DB — Config & Stats
    getDatabaseStats(): Promise<{ ok: { total_embeddings: number } } | { err: string }>;
    getConfigsByIp(ip: string): Promise<Array<[string, string, string, Record<string, string>]>>;

    // Timezone
    getTimezoneConfig(): Promise<TimezoneConfig>;
    setTimezoneConfig(utcOffsetHours: number, label: string, description: string): Promise<Result<null, string>>;

    // Credentials
    generateJitCredential(credentialType: CredentialType, target: string, duration: bigint, name?: string | null): Promise<Result<{ id: string; credential: JitCredential }, string>>;
    getActiveCredentials(): Promise<Result<JitCredential[], string>>;

    // Knowledge Base
    getKBStatistics(): Promise<KBStatistics>;
    listKBDocuments(docType?: DocumentType): Promise<KBDocument[]>;
    getKBDocument(id: string): Promise<KBDocument>;
    searchKB(request: SearchRequest): Promise<KBDocument[]>;
    insertKBDocument(request: StoreDocumentRequest): Promise<Result<string, string>>;

    // Claude / LLM
    isClaudeConfigured(): Promise<boolean>;
    setClaudeApiKey(apiKey: string): Promise<void>;
    chatWithClaude(request: ChatRequest): Promise<Result<ChatResponse, string>>;
    getApiKeyForFrontend(): Promise<Result<string, string>>;
    getLLMEndpoints(): Promise<Result<LLMEndpoint[], string>>;
    testLLMEndpoint(name: string): Promise<string>;
    addLLMEndpoint(endpoint: LLMEndpoint): Promise<void>;
    updateLLMEndpoint(endpoint: LLMEndpoint): Promise<void>;
    removeLLMEndpoint(name: string): Promise<void>;
    enableLLMEndpoint(name: string, enabled: boolean): Promise<void>;

    // Activity Logs
    getDeviceActivityLogs(deviceId: string, limit?: number, offset?: number): Promise<CandidResult<ActivityLogListResponse>>;
    addDeviceActivityLog(request: AddActivityLogRequest): Promise<CandidResult<DeviceActivityLog>>;
    getAllActivityLogs(limit?: number, offset?: number): Promise<CandidResult<ActivityLogListResponse>>;
    getActivityLogStatistics(): Promise<CandidResult<ActivityLogStatistics>>;

    // Interview / Questionnaire
    addQuestionnaireResponse(serviceId: string, section: string, components: QuestionnaireComponent[], submittedBy: string, notes: string | null): Promise<CandidResult<QuestionnaireResponse>>;
    getQuestionnaireResponsesForService(serviceId: string): Promise<QuestionnaireResponse[]>;
    getDocumentsForService(serviceId: string): Promise<ServiceDocument[]>;
    getSessionsForService(serviceId: string): Promise<InterviewSession[]>;
    startInterviewSession(sessionId: string): Promise<CandidResult<InterviewSession>>;

    // Enterprise Architecture
    bulkImportEA(request: BulkEAImport): Promise<BulkEAImportResult>;
    getEAStatistics(): Promise<EAStatistics>;
    listEAOrganizations(): Promise<EAOrganization[]>;
    listEAApplications(): Promise<EAApplicationEntity[]>;
    listEATechnicalServices(): Promise<EATechnicalService[]>;
    listEABusinessServices(): Promise<EABusinessServiceEntity[]>;
    listEADataObjects(): Promise<EADataObject[]>;
    listEAHRRoles(): Promise<EAHRRole[]>;
    getEARelationshipsFor(sourceType: string, sourceId: string): Promise<EARelationship[]>;
    listEARelationships(): Promise<EARelationship[]>;
    listEACapabilities(): Promise<EACapability[]>;
    listEAProcesses(): Promise<EAProcess[]>;
    listEAProcessConnectors(): Promise<EAProcessConnector[]>;
    listEACompliance(): Promise<EACompliance[]>;
    listEAIntegrations(): Promise<EAIntegration[]>;
    clearEAData(): Promise<string>;
}

// ============================================================================
// CanisterClient Class
// ============================================================================

/**
 * Main client class for interacting with IC canisters
 * Provides both direct canister calls and proxy API access
 *
 * Sprint 31: Environment-aware communication
 * - Automatically detects local vs mainnet environment
 * - Uses ProxyAdapter for local, AgentAdapter for mainnet
 */
export class CanisterClient implements CanisterClientInterface {
    private icHost: string;
    private proxyUrl: string;
    private canisterIds: CanisterIds;
    private adapter: CommunicationAdapter | null = null;
    private adapterInitialized: boolean = false;

    constructor() {
        // Sprint 31: Use environment-aware configuration
        this.icHost = environment.icHost;
        this.proxyUrl = environment.proxyUrl;

        // Log environment detection
        console.log(`🌐 Environment: ${environment.mode} (${isMainnet() ? 'mainnet' : 'local'})`);
        console.log(`📡 IC Host: ${this.icHost}`);

        // Initialize canister IDs from centralized registry
        // Falls back to local IDs if mainnet not configured
        this.canisterIds = {
            aws_discovery_integration: getCanisterIdOrDefault('aws_discovery_integration'),
            device_manager: getCanisterIdOrDefault('vector_db'),  // Deprecated - maps to vector_db
            vector_db: getCanisterIdOrDefault('vector_db'),
            credential_manager: getCanisterIdOrDefault('credential_manager'),
            blockchain_storage: getCanisterIdOrDefault('blockchain_storage'),
            user_manager: getCanisterIdOrDefault('user_manager'),
            ii_agent: getCanisterIdOrDefault('ii_agent'),
            service_mapper: getCanisterIdOrDefault('service_mapper'),
            knowledge_base: getCanisterIdOrDefault('knowledge_base'),
            claude_proxy: getCanisterIdOrDefault('claude_proxy'),
            llm_proxy: getCanisterIdOrDefault('llm_proxy'),
            ada: getCanisterIdOrDefault('ada')
        };

        console.log('✅ Canister IDs initialized from registry:', this.canisterIds);

        // Initialize communication adapter asynchronously
        this.initializeAdapterAsync();
    }

    /**
     * Initialize the communication adapter asynchronously
     * Called from constructor, but allows operations to proceed before completion
     */
    private async initializeAdapterAsync(): Promise<void> {
        try {
            this.adapter = await initializeAdapter();
            this.adapterInitialized = true;
            console.log(`✅ Communication adapter ready: ${this.adapter.name}`);
        } catch (error) {
            console.warn('⚠️ Adapter initialization failed, will retry on first call:', error);
        }
    }

    /**
     * Ensure adapter is initialized before making calls
     */
    private async ensureAdapter(): Promise<CommunicationAdapter> {
        if (!this.adapterInitialized || !this.adapter) {
            this.adapter = await initializeAdapter();
            this.adapterInitialized = true;
        }
        return this.adapter;
    }

    /**
     * Helper to safely stringify objects containing BigInt
     * Converts BigInt to string for JSON serialization
     */
    private safeStringify(obj: unknown): string {
        return JSON.stringify(obj, (_key, value) =>
            typeof value === 'bigint' ? value.toString() : value
        );
    }

    /**
     * Generic method to call any canister function
     * Supports both real canister calls via proxy and mock implementations
     */
    async callCanister<T = unknown>(
        canisterName: keyof CanisterIds,
        methodName: string,
        args: unknown[] = []
    ): Promise<T> {
        console.log(`🔧 Calling canister: ${canisterName}.${methodName}`);
        console.log(`🔧 Available canister IDs:`, this.canisterIds);

        const canisterId = this.canisterIds[canisterName];
        if (!canisterId) {
            console.error(`❌ Canister ID not found for '${canisterName}'`);
            console.error(`❌ Available canisters:`, Object.keys(this.canisterIds));
            throw new Error(`Canister ID not found for ${canisterName}`);
        }

        console.log(`✅ Found canister ID for ${canisterName}: ${canisterId}`);

        // Generate the DFX command for this call (for debugging)
        const networkFlag = isMainnet() ? 'ic' : 'local';
        const argsStr = args.length > 0 ? `'(${args.map(arg =>
            typeof arg === 'string' ? `"${arg}"` :
            typeof arg === 'bigint' ? arg.toString() :
            this.safeStringify(arg)
        ).join(', ')})'` : '';
        const command = `dfx canister call ${canisterName} ${methodName} ${argsStr} --network ${networkFlag}`;

        console.log(`🔧 DFX Command: ${command}`);

        // Always use real canister calls - no mocking
        console.log('🔗 Making REAL canister call');
        return await this.makeRealCanisterCall<T>(canisterName, methodName, args);
    }

    /**
     * Make canister call using the appropriate adapter
     * Sprint 31: Uses ProxyAdapter (local) or AgentAdapter (mainnet)
     */
    private async makeRealCanisterCall<T>(
        canisterName: string,
        methodName: string,
        args: unknown[]
    ): Promise<T> {
        // Ensure adapter is ready
        const adapter = await this.ensureAdapter();

        if (environment.debug) {
            console.log(`📡 [${adapter.name}] Calling ${canisterName}.${methodName}`);
        }

        try {
            // Use the communication adapter for the call
            const result = await adapter.call<T>({
                canisterName: canisterName as CanisterName,
                methodName,
                args,
                timeout: 90000  // 90 second timeout for LLM calls
            });

            if (environment.debug) {
                console.log('✅ Canister response:', result);
            }

            return result;
        } catch (error: unknown) {
            console.error(`❌ Canister call failed: ${canisterName}.${methodName}`, error);
            throw error;
        }
    }


    // ========================================================================
    // Discovery Integration Methods
    // ========================================================================

    /**
     * Collect device inventory by device ID
     */
    async collectDeviceInventory(deviceId: string): Promise<Result<DeviceInventory, string>> {
        return await this.callCanister<Result<DeviceInventory, string>>(
            'aws_discovery_integration',
            'collect_device_inventory',
            [deviceId]
        );
    }

    /**
     * Collect device inventory by IP address (standard network discovery approach)
     */
    async collectDeviceInventoryByIP(ipAddress: string): Promise<Result<DeviceInventory, string>> {
        return await this.callCanister<Result<DeviceInventory, string>>(
            'aws_discovery_integration',
            'collect_device_inventory_by_ip',
            [ipAddress]
        );
    }

    /**
     * Discover all devices via EC2 Discovery Hub
     */
    async discoverAllDevices(): Promise<Result<DiscoveryHubResponse[], string>> {
        return await this.callCanister<Result<DiscoveryHubResponse[], string>>(
            'aws_discovery_integration',
            'discover_all_devices_via_hub',
            []
        );
    }

    /**
     * Alias for compatibility - discover all devices via hub
     */
    async discoverAllDevicesViaHub(): Promise<Result<DiscoveryHubResponse[], string>> {
        return await this.discoverAllDevices();
    }

    /**
     * Get discovery results from last scan
     */
    async getDiscoveryResults(): Promise<DiscoveryResult[]> {
        return await this.callCanister<DiscoveryResult[]>(
            'aws_discovery_integration',
            'get_discovery_results',
            []
        );
    }

    /**
     * Set Discovery Hub configuration URL
     */
    async setDiscoveryHubConfig(hubUrl: string): Promise<Result<null, string>> {
        return await this.callCanister<Result<null, string>>(
            'aws_discovery_integration',
            'set_discovery_hub_config',
            [hubUrl]
        );
    }

    /**
     * Get current Discovery Hub configuration
     */
    async getDiscoveryHubConfig(): Promise<Result<string, string>> {
        return await this.callCanister<Result<string, string>>(
            'aws_discovery_integration',
            'get_discovery_hub_config',
            []
        );
    }

    /**
     * Test Discovery Hub availability
     */
    async testDiscoveryHub(): Promise<{ success: boolean; message: string; dfx_command: string; devices_configured: number }> {
        return await this.callCanister(
            'aws_discovery_integration',
            'test_discovery_hub',
            []
        );
    }

    /**
     * Test Discovery Hub connection with timeout
     */
    async testDiscoveryHubConnection(): Promise<Result<string, string>> {
        return await this.callCanister<Result<string, string>>(
            'aws_discovery_integration',
            'test_discovery_hub_connection',
            []
        );
    }

    /**
     * Direct call to Discovery Hub (bypassing canister)
     * Uses hardcoded hub URL for reliability
     */
    async discoverViaDirectHub(ipAddress: string): Promise<DirectHubResult> {
        const hubUrl = 'https://unboundata.com';
        try {
            console.log(`🌐 Direct call to Discovery Hub: ${hubUrl}/discover/ip/${ipAddress}`);
            const response = await fetch(`${hubUrl}/discover/ip/${ipAddress}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('🌐 Discovery Hub result:', result);
            return {
                success: true,
                data: result,
                source: 'direct_hub'
            };
        } catch (error: unknown) {
            console.error('❌ Direct Discovery Hub call failed:', error);
            return {
                success: false,
                error: (error as Error).message,
                source: 'direct_hub'
            };
        }
    }

    /**
     * Discover multiple IPs via direct hub calls (batched)
     * Includes progress callback for UI updates
     */
    async discoverRangeViaDirectHub(
        ipList: string[],
        logCallback: LogCallback | null = null
    ): Promise<DirectHubResult> {
        const hubUrl = 'https://unboundata.com';
        try {
            console.log(`🌐 Range discovery via multiple single calls: ${ipList.length} IPs`);

            // Limit concurrent requests to avoid overwhelming the server
            const batchSize = 10;
            const results: DiscoveryBatchResult[] = [];

            for (let i = 0; i < ipList.length; i += batchSize) {
                const batch = ipList.slice(i, i + batchSize);
                const batchNumber = Math.floor(i / batchSize) + 1;
                const totalBatches = Math.ceil(ipList.length / batchSize);

                console.log(`Processing batch ${batchNumber}/${totalBatches}: ${batch.length} IPs`);

                // Log batch progress if callback provided
                if (logCallback) {
                    logCallback(`📦 Processing batch ${batchNumber}/${totalBatches}: ${batch.length} IPs`, 'progress');
                }

                const batchPromises = batch.map(async (ip): Promise<DiscoveryBatchResult> => {
                    try {
                        const response = await fetch(`${hubUrl}/discover/ip/${ip}`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        });

                        if (!response.ok) {
                            return {
                                ip: ip,
                                status: 'failed',
                                error: `HTTP ${response.status}`
                            };
                        }

                        const result = await response.json();
                        return {
                            ip: ip,
                            status: result.status || 'discovered',
                            inventory: result.inventory || result
                        };
                    } catch (error: unknown) {
                        return {
                            ip: ip,
                            status: 'failed',
                            error: (error as Error).message
                        };
                    }
                });

                const batchResults = await Promise.all(batchPromises);
                results.push(...batchResults);

                // Log batch completion with graduated discovery statuses
                if (logCallback) {
                    // Count different discovery levels
                    const statusCounts = {
                        full: batchResults.filter(r => r.status === 'full').length,
                        partial: batchResults.filter(r => r.status === 'partial').length,
                        port_scan: batchResults.filter(r => r.status === 'port_scan').length,
                        auth_failed: batchResults.filter(r => r.status === 'auth_failed').length,
                        offline: batchResults.filter(r => r.status === 'offline').length,
                        discovered: batchResults.filter(r => r.status === 'discovered').length // Legacy status
                    };

                    const totalSuccessful = statusCounts.full + statusCounts.partial + statusCounts.discovered;
                    logCallback(`✅ Batch ${batchNumber} complete: ${totalSuccessful}/${batch.length} devices discovered`, 'success');

                    // Log individual devices with graduated status indicators
                    batchResults.forEach(device => {
                        const hostname = device.inventory?.hostname || '';
                        const displayName = hostname ? hostname : device.ip;

                        switch(device.status) {
                            case 'full':
                                logCallback(`   🎯 ${device.ip}: ${displayName} (full discovery)`, 'detail');
                                break;
                            case 'partial':
                                logCallback(`   📍 ${device.ip}: ${displayName} (partial data)`, 'detail');
                                break;
                            case 'port_scan':
                                logCallback(`   🔌 ${device.ip}: ports detected only`, 'detail');
                                break;
                            case 'auth_failed':
                                logCallback(`   🔐 ${device.ip}: authentication failed`, 'detail');
                                break;
                            case 'offline':
                                logCallback(`   ⚫ ${device.ip}: offline`, 'detail');
                                break;
                            case 'discovered':
                                // Legacy status - treat as full discovery
                                logCallback(`   📛 ${device.ip}: ${displayName}`, 'detail');
                                break;
                            default:
                                logCallback(`   ❓ ${device.ip}: ${device.status}`, 'detail');
                        }
                    });
                }

                // Small delay between batches to be nice to the server
                if (i + batchSize < ipList.length) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            console.log(`🌐 Range discovery completed: ${results.length} results`);
            return {
                success: true,
                data: results,
                source: 'direct_hub_batch'
            };
        } catch (error: unknown) {
            console.error('❌ Direct range discovery failed:', error);
            return {
                success: false,
                error: (error as Error).message,
                source: 'direct_hub_batch'
            };
        }
    }

    // ========================================================================
    // Device Manager Methods
    // ========================================================================

    /**
     * Add a new device to the registry
     */
    async addDevice(device: Device): Promise<Result<string, string>> {
        return await this.callCanister<Result<string, string>>(
            'device_manager',
            'add_device',
            [device]
        );
    }

    /**
     * List all registered devices
     */
    async listDevices(): Promise<Result<Device[], string>> {
        return await this.callCanister<Result<Device[], string>>(
            'device_manager',
            'list_devices',
            []
        );
    }

    // ========================================================================
    // Vector DB Methods
    // ========================================================================

    /**
     * Search for similar devices using metadata
     */
    async searchSimilar(query: Record<string, string>, limit: number): Promise<Result<DeviceTuple[], string>> {
        return await this.callCanister<Result<DeviceTuple[], string>>(
            'vector_db',
            'search_similar',
            [query, limit]
        );
    }

    /**
     * Get all devices from vector database
     * Sprint 36: Uses AgentAdapter for both local and mainnet
     */
    async getAllDevices(): Promise<{ ok: DeviceTuple[] } | { err: string }> {
        return await this.callCanister<{ ok: DeviceTuple[] } | { err: string }>(
            'vector_db',
            'get_all_devices',
            []
        );
    }

    /**
     * Get all applications from vector database
     * Sprint 36: Uses AgentAdapter for both local and mainnet
     */
    async getAllApplications(): Promise<{ ok: ApplicationTuple[] } | { err: string }> {
        return await this.callCanister<{ ok: ApplicationTuple[] } | { err: string }>(
            'vector_db',
            'get_all_applications',
            []
        );
    }

    /**
     * Get database statistics
     * Sprint 36: Uses AgentAdapter for both local and mainnet
     */
    async getDatabaseStats(): Promise<{ ok: { total_embeddings: number } } | { err: string }> {
        return await this.callCanister<{ ok: { total_embeddings: number } } | { err: string }>(
            'vector_db',
            'get_database_stats',
            []
        );
    }

    /**
     * Store configuration file in vector database
     */
    async storeConfig(
        configId: string,
        ip: string,
        path: string,
        content: string,
        configType: string,
        embedding: Float32Array,
        metadata: Record<string, string>
    ): Promise<Result<null, string>> {
        return await this.callCanister<Result<null, string>>(
            'vector_db',
            'store_config',
            [configId, ip, path, content, configType, embedding, metadata]
        );
    }

    /**
     * Add device embedding to vector database
     * Returns the generated sys_id for the device
     */
    async addDeviceEmbedding(deviceId: string, metadata: Array<[string, string]>): Promise<Result<string, string>> {
        return await this.callCanister<Result<string, string>>(
            'vector_db',
            'add_embedding',
            [deviceId, metadata]
        );
    }

    /**
     * Store complete discovered device data using Storage Manager (OOA/OOD Design)
     *
     * This is the centralized entry point for storing all discovery data:
     * - Device embedding and metadata
     * - Configuration files with embeddings
     * - Hardware/software inventory
     * - Applications and relationships
     *
     * @param request Complete storage request with all discovery data
     * @returns StorageResponse with summary and any warnings
     */
    async storeDiscoveredDevice(request: StorageRequest): Promise<CandidResult<StorageResponse>> {
        return await this.callCanister<CandidResult<StorageResponse>>(
            'vector_db',
            'store_discovered_device',
            [request]
        );
    }

    /**
     * Get devices filtered by type
     */
    async getDevicesByType(deviceType: string): Promise<Result<DeviceTuple[], string>> {
        return await this.callCanister<Result<DeviceTuple[], string>>(
            'vector_db',
            'get_devices_by_type',
            [deviceType]
        );
    }

    /**
     * Get device by IP address
     * Used by service map to look up device details when clicking a node
     */
    async getDeviceByIp(ip: string): Promise<DeviceTuple[] | null> {
        return await this.callCanister<DeviceTuple[] | null>(
            'vector_db',
            'get_device_by_ip',
            [ip]
        );
    }

    /**
     * Get all device-application relationships
     * Sprint 36: Uses AgentAdapter for both local and mainnet
     */
    async getAllRelationships(): Promise<{ ok: DeviceApplicationRelationship[] } | { err: string }> {
        return await this.callCanister<{ ok: DeviceApplicationRelationship[] } | { err: string }>(
            'vector_db',
            'get_all_relationships',
            []
        );
    }

    /**
     * Get all configuration files for a specific device by IP address
     * Returns config_id, path, config_type, and metadata for each config
     * Sprint 36: Uses AgentAdapter for both local and mainnet
     */
    async getConfigsByIp(ip: string): Promise<Array<[string, string, string, Record<string, string>]>> {
        try {
            return await this.callCanister<Array<[string, string, string, Record<string, string>]>>(
                'vector_db',
                'get_configs_by_ip',
                [ip]
            );
        } catch (error: unknown) {
            console.error('Error getting configs by IP:', error);
            return [];
        }
    }

    // ========================================================================
    // Timezone Configuration Service Methods
    // ========================================================================

    /**
     * Get Timezone Configuration from vector_db service
     * Following SOA principles - consumes centralized Timezone Configuration Service
     * Implements caching to minimize inter-canister calls
     */
    async getTimezoneConfig(): Promise<TimezoneConfig> {
        return await this.callCanister<TimezoneConfig>(
            'vector_db',
            'get_timezone_config',
            []
        );
    }

    /**
     * Set Timezone Configuration in vector_db service
     * Following SOA principles - updates centralized Timezone Configuration Service
     * After successful update, consumers should refresh their local caches
     */
    async setTimezoneConfig(
        utcOffsetHours: number,
        label: string,
        description: string
    ): Promise<Result<null, string>> {
        return await this.callCanister<Result<null, string>>(
            'vector_db',
            'set_timezone_config',
            [utcOffsetHours, label, description]
        );
    }

    // ========================================================================
    // Credential Manager Methods
    // ========================================================================

    /**
     * Generate Just-In-Time credential with automatic expiration
     */
    async generateJitCredential(
        credentialType: CredentialType,
        target: string,
        duration: bigint,
        name: string | null = null
    ): Promise<Result<{ id: string; credential: JitCredential }, string>> {
        return await this.callCanister<Result<{ id: string; credential: JitCredential }, string>>(
            'credential_manager',
            'generate_jit_credential',
            [credentialType, target, duration, name]
        );
    }

    /**
     * Get all active (non-expired) credentials
     */
    async getActiveCredentials(): Promise<Result<JitCredential[], string>> {
        return await this.callCanister<Result<JitCredential[], string>>(
            'credential_manager',
            'get_active_jit_credentials',
            []
        );
    }

    // ========================================================================
    // Knowledge Base Methods
    // ========================================================================

    /**
     * Get Knowledge Base statistics (document counts by type)
     */
    async getKBStatistics(): Promise<KBStatistics> {
        return await this.callCanister<KBStatistics>(
            'knowledge_base',
            'get_statistics',
            []
        );
    }

    /**
     * List all KB documents, optionally filtered by document type
     */
    async listKBDocuments(docType?: DocumentType): Promise<KBDocument[]> {
        // Candid opt type: [] for None, [value] for Some
        const optDocType = docType ? [docType] : [];
        const result = await this.callCanister<KBDocument[]>(
            'knowledge_base',
            'list_documents',
            [optDocType]
        );

        return Array.isArray(result) ? result : [];
    }

    /**
     * Get a specific KB document by ID
     */
    async getKBDocument(id: string): Promise<KBDocument> {
        const result = await this.callCanister<CandidResult<KBDocument>>(
            'knowledge_base',
            'get_document',
            [id]
        );

        if ('Err' in result) {
            throw new Error(result.Err);
        }

        return (result as { Ok: KBDocument }).Ok;
    }

    /**
     * Search Knowledge Base with optional filters
     */
    async searchKB(request: SearchRequest): Promise<KBDocument[]> {
        return await this.callCanister<KBDocument[]>(
            'knowledge_base',
            'search',
            [request]
        );
    }

    /**
     * Insert or update a KB document
     */
    async insertKBDocument(request: StoreDocumentRequest): Promise<Result<string, string>> {
        const result = await this.callCanister<{ Ok?: string; Err?: string }>(
            'knowledge_base',
            'store_document',
            [request]
        );

        if ('Err' in result) {
            return { err: result.Err };
        }

        return { ok: result.Ok! };
    }

    /**
     * Export all KB documents
     */
    async exportAllKBDocuments(): Promise<KBDocument[]> {
        return await this.callCanister<KBDocument[]>(
            'knowledge_base',
            'export_all_documents',
            []
        );
    }

    /**
     * Get document version history
     */
    async getDocumentHistory(id: string): Promise<VersionHistory> {
        const result = await this.callCanister<{ Ok?: VersionHistory; Err?: string }>(
            'knowledge_base',
            'get_document_history',
            [id]
        );

        if ('Err' in result) {
            throw new Error(result.Err);
        }

        return result.Ok!;
    }

    // ========================================================================
    // Claude Proxy Methods
    // ========================================================================

    /**
     * Chat with Claude AI
     * Sends messages to Claude and gets intelligent responses about infrastructure
     * Note: Candid opt types use array syntax: [] for None, [value] for Some(value)
     * This matches the generated TypeScript declarations from dfx generate
     */
    async chatWithClaude(request: ChatRequest): Promise<Result<ChatResponse, string>> {
        return await this.callCanister<Result<ChatResponse, string>>(
            'claude_proxy',
            'chat',
            [request]
        );
    }

    /**
     * Get Claude usage statistics
     * Returns total queries, tokens used, and cost information
     */
    async getClaudeUsageStats(): Promise<UsageStats> {
        return await this.callCanister<UsageStats>(
            'claude_proxy',
            'get_usage_statistics',
            []
        );
    }

    /**
     * Check if Claude is configured
     * Returns true if API key is set
     */
    async isClaudeConfigured(): Promise<boolean> {
        return await this.callCanister<boolean>(
            'claude_proxy',
            'is_configured',
            []
        );
    }

    /**
     * Set Claude API key (admin only)
     * Configures the Anthropic API key for Claude integration
     */
    async setClaudeApiKey(apiKey: string): Promise<void> {
        await this.callCanister<void>(
            'claude_proxy',
            'set_api_key',
            [apiKey]
        );
    }

    /**
     * Sprint 31: Get API key for frontend-assisted mode (admin only)
     * Returns the API key so frontend can call Claude directly on mainnet
     */
    async getApiKeyForFrontend(): Promise<Result<string, string>> {
        return await this.callCanister<Result<string, string>>(
            'claude_proxy',
            'get_api_key_for_frontend',
            []
        );
    }

    /**
     * Sprint 31: Chat with frontend-assisted mode (for ICP mainnet)
     * Frontend calls Claude directly, then passes response here for tracking/storage
     */
    async chatFrontendAssisted(request: FrontendAssistedRequest): Promise<Result<ChatResponse, string>> {
        return await this.callCanister<Result<ChatResponse, string>>(
            'claude_proxy',
            'chat_frontend_assisted',
            [request]
        );
    }

    /**
     * Sprint 31: Get Claude model name
     */
    async getClaudeModelName(): Promise<string> {
        return await this.callCanister<string>(
            'claude_proxy',
            'get_model_name',
            []
        );
    }

    // ========================================================================
    // LLM Proxy Methods (Self-Hosted LLM Support)
    // ========================================================================

    /**
     * Chat with self-hosted LLM
     * Sends messages to configured LLM endpoints with smart routing
     */
    async chatWithLLM(request: LLMChatRequest): Promise<Result<LLMChatResponse, string>> {
        return await this.callCanister<Result<LLMChatResponse, string>>(
            'llm_proxy',
            'chat',
            [request]
        );
    }

    /**
     * Get LLM usage statistics
     * Returns query counts and token usage per backend type
     */
    async getLLMUsageStats(): Promise<LLMUsageStats> {
        return await this.callCanister<LLMUsageStats>(
            'llm_proxy',
            'get_usage_statistics',
            []
        );
    }

    /**
     * Check if LLM proxy is configured
     * Returns true if at least one endpoint is enabled
     */
    async isLLMConfigured(): Promise<boolean> {
        return await this.callCanister<boolean>(
            'llm_proxy',
            'is_configured',
            []
        );
    }

    /**
     * Get all configured LLM endpoints
     * Returns list of all LLM endpoints with their configuration
     */
    async getLLMEndpoints(): Promise<Result<LLMEndpoint[], string>> {
        return await this.callCanister<Result<LLMEndpoint[], string>>(
            'llm_proxy',
            'get_llm_endpoints',
            []
        );
    }

    /**
     * Add a new LLM endpoint (admin only)
     * Configures a new self-hosted LLM endpoint
     */
    async addLLMEndpoint(endpoint: LLMEndpoint): Promise<void> {
        await this.callCanister<void>(
            'llm_proxy',
            'add_llm_endpoint',
            [endpoint]
        );
    }

    /**
     * Update an existing LLM endpoint (admin only)
     */
    async updateLLMEndpoint(endpoint: LLMEndpoint): Promise<void> {
        await this.callCanister<void>(
            'llm_proxy',
            'update_llm_endpoint',
            [endpoint]
        );
    }

    /**
     * Remove an LLM endpoint (admin only)
     */
    async removeLLMEndpoint(name: string): Promise<void> {
        await this.callCanister<void>(
            'llm_proxy',
            'remove_llm_endpoint',
            [name]
        );
    }

    /**
     * Enable or disable an LLM endpoint (admin only)
     */
    async enableLLMEndpoint(name: string, enabled: boolean): Promise<void> {
        await this.callCanister<void>(
            'llm_proxy',
            'enable_llm_endpoint',
            [name, enabled]
        );
    }

    /**
     * Test connectivity to an LLM endpoint
     * Sends a simple health check request
     */
    async testLLMEndpoint(name: string): Promise<string> {
        return await this.callCanister<string>(
            'llm_proxy',
            'test_endpoint',
            [name]
        );
    }

    /**
     * Add questionnaire response
     */
    async addQuestionnaireResponse(
        serviceId: string,
        section: string,
        components: QuestionnaireComponent[],
        submittedBy: string,
        notes: string | null
    ): Promise<CandidResult<QuestionnaireResponse>> {
        return await this.callCanister<CandidResult<QuestionnaireResponse>>(
            'vector_db',
            'add_questionnaire_response',
            [serviceId, section, components, submittedBy, notes ? [notes] : []]
        );
    }

    /**
     * Get questionnaire responses for a service
     */
    async getQuestionnaireResponsesForService(serviceId: string): Promise<QuestionnaireResponse[]> {
        return await this.callCanister<QuestionnaireResponse[]>(
            'vector_db',
            'get_questionnaire_responses_for_service',
            [serviceId]
        );
    }

    /**
     * Upload service document
     */
    async uploadServiceDocument(
        serviceId: string,
        documentType: ServiceDocumentType,
        filename: string,
        contentType: string,
        content: Uint8Array | null,
        uploadedBy: string
    ): Promise<CandidResult<ServiceDocument>> {
        return await this.callCanister<CandidResult<ServiceDocument>>(
            'vector_db',
            'upload_service_document',
            [serviceId, documentType, filename, contentType, content ? [Array.from(content)] : [], uploadedBy]
        );
    }

    /**
     * Get documents for a service
     */
    async getDocumentsForService(serviceId: string): Promise<ServiceDocument[]> {
        return await this.callCanister<ServiceDocument[]>(
            'vector_db',
            'get_documents_for_service',
            [serviceId]
        );
    }

    /**
     * Create interview session
     */
    async createInterviewSession(
        serviceId: string,
        participantName: string,
        participantRole: string,
        sessionType: string,
        sections: string[],
        scheduledAt: bigint | null
    ): Promise<CandidResult<InterviewSession>> {
        return await this.callCanister<CandidResult<InterviewSession>>(
            'vector_db',
            'create_interview_session',
            [serviceId, participantName, participantRole, sessionType, sections, scheduledAt ? [scheduledAt] : []]
        );
    }

    /**
     * Get interview sessions for a service
     */
    async getSessionsForService(serviceId: string): Promise<InterviewSession[]> {
        return await this.callCanister<InterviewSession[]>(
            'vector_db',
            'get_sessions_for_service',
            [serviceId]
        );
    }

    /**
     * Start an interview session
     */
    async startInterviewSession(sessionId: string): Promise<CandidResult<InterviewSession>> {
        return await this.callCanister<CandidResult<InterviewSession>>(
            'vector_db',
            'start_interview_session',
            [sessionId]
        );
    }

    // ========================================================================
    // Sprint 29: Device Activity Log Methods
    // ========================================================================

    /**
     * Get activity logs for a specific device
     * Returns paginated list of activity logs sorted by timestamp (newest first)
     */
    async getDeviceActivityLogs(
        deviceId: string,
        limit: number = 50,
        offset: number = 0
    ): Promise<CandidResult<ActivityLogListResponse>> {
        return await this.callCanister<CandidResult<ActivityLogListResponse>>(
            'vector_db',
            'get_device_activity_logs',
            [deviceId, BigInt(limit), BigInt(offset)]
        );
    }

    /**
     * Add a new activity log entry
     * Used for manual actions like viewing device details
     */
    async addDeviceActivityLog(request: AddActivityLogRequest): Promise<CandidResult<DeviceActivityLog>> {
        return await this.callCanister<CandidResult<DeviceActivityLog>>(
            'vector_db',
            'add_device_activity_log',
            [request]
        );
    }

    /**
     * Get all activity logs with pagination
     */
    async getAllActivityLogs(
        limit: number = 100,
        offset: number = 0
    ): Promise<CandidResult<ActivityLogListResponse>> {
        return await this.callCanister<CandidResult<ActivityLogListResponse>>(
            'vector_db',
            'get_all_activity_logs',
            [BigInt(limit), BigInt(offset)]
        );
    }

    /**
     * Search activity logs by query string
     */
    async searchActivityLogs(
        query: string,
        deviceId: string | null = null,
        limit: number = 50
    ): Promise<CandidResult<DeviceActivityLog[]>> {
        return await this.callCanister<CandidResult<DeviceActivityLog[]>>(
            'vector_db',
            'search_activity_logs',
            [query, deviceId ? [deviceId] : [], BigInt(limit)]
        );
    }

    /**
     * Get activity log statistics
     */
    async getActivityLogStatistics(): Promise<CandidResult<ActivityLogStatistics>> {
        return await this.callCanister<CandidResult<ActivityLogStatistics>>(
            'vector_db',
            'get_activity_log_statistics',
            []
        );
    }

    /**
     * Seed dummy activity log data for testing
     */
    async seedActivityLogData(deviceId: string): Promise<CandidResult<DeviceActivityLog[]>> {
        return await this.callCanister<CandidResult<DeviceActivityLog[]>>(
            'vector_db',
            'seed_activity_log_data',
            [deviceId]
        );
    }

    // ========================================================================
    // Sprint 35: Enterprise Architecture Methods
    // ========================================================================

    /**
     * Bulk import EA data (organizations, applications, services, etc.)
     */
    async bulkImportEA(request: BulkEAImport): Promise<BulkEAImportResult> {
        return await this.callCanister<BulkEAImportResult>(
            'vector_db',
            'bulk_import_ea',
            [request]
        );
    }

    /**
     * Get EA statistics (entity counts, headcount breakdowns)
     */
    async getEAStatistics(): Promise<EAStatistics> {
        return await this.callCanister<EAStatistics>(
            'vector_db',
            'get_ea_statistics',
            []
        );
    }

    /**
     * List all EA organizations
     */
    async listEAOrganizations(): Promise<EAOrganization[]> {
        return await this.callCanister<EAOrganization[]>(
            'vector_db',
            'list_ea_organizations',
            []
        );
    }

    /**
     * List all EA applications
     */
    async listEAApplications(): Promise<EAApplicationEntity[]> {
        return await this.callCanister<EAApplicationEntity[]>(
            'vector_db',
            'list_ea_applications',
            []
        );
    }

    /**
     * List all EA technical services
     */
    async listEATechnicalServices(): Promise<EATechnicalService[]> {
        return await this.callCanister<EATechnicalService[]>(
            'vector_db',
            'list_ea_technical_services',
            []
        );
    }

    /**
     * List all EA business services
     */
    async listEABusinessServices(): Promise<EABusinessServiceEntity[]> {
        return await this.callCanister<EABusinessServiceEntity[]>(
            'vector_db',
            'list_ea_business_services',
            []
        );
    }

    /**
     * List all EA data objects
     */
    async listEADataObjects(): Promise<EADataObject[]> {
        return await this.callCanister<EADataObject[]>(
            'vector_db',
            'list_ea_data_objects',
            []
        );
    }

    /**
     * List all EA HR roles
     */
    async listEAHRRoles(): Promise<EAHRRole[]> {
        return await this.callCanister<EAHRRole[]>(
            'vector_db',
            'list_ea_hr_roles',
            []
        );
    }

    /**
     * Get EA relationships for a specific entity
     */
    async getEARelationshipsFor(sourceType: string, sourceId: string): Promise<EARelationship[]> {
        return await this.callCanister<EARelationship[]>(
            'vector_db',
            'get_ea_relationships_for',
            [sourceType, sourceId]
        );
    }

    /**
     * List all EA relationships
     */
    async listEARelationships(): Promise<EARelationship[]> {
        return await this.callCanister<EARelationship[]>(
            'vector_db',
            'list_ea_relationships',
            []
        );
    }

    // Sprint 45: New EA Entity Type List Methods

    /**
     * List all EA capabilities
     */
    async listEACapabilities(): Promise<EACapability[]> {
        return await this.callCanister<EACapability[]>(
            'vector_db',
            'list_ea_capabilities',
            []
        );
    }

    /**
     * List all EA processes
     */
    async listEAProcesses(): Promise<EAProcess[]> {
        return await this.callCanister<EAProcess[]>(
            'vector_db',
            'list_ea_processes',
            []
        );
    }

    /**
     * List all EA process connectors
     */
    async listEAProcessConnectors(): Promise<EAProcessConnector[]> {
        return await this.callCanister<EAProcessConnector[]>(
            'vector_db',
            'list_ea_process_connectors',
            []
        );
    }

    /**
     * List all EA compliance obligations
     */
    async listEACompliance(): Promise<EACompliance[]> {
        return await this.callCanister<EACompliance[]>(
            'vector_db',
            'list_ea_compliance',
            []
        );
    }

    /**
     * List all EA integrations
     */
    async listEAIntegrations(): Promise<EAIntegration[]> {
        return await this.callCanister<EAIntegration[]>(
            'vector_db',
            'list_ea_integrations',
            []
        );
    }

    /**
     * Clear all EA data
     */
    async clearEAData(): Promise<string> {
        return await this.callCanister<string>(
            'vector_db',
            'clear_ea_data',
            []
        );
    }
}

// ============================================================================
// Browser Integration
// ============================================================================

// Export class for module system
/**
 * Extended CanisterClient with cache-busting metadata
 */
interface CanisterClientWithMeta extends CanisterClient {
    cacheVersion: string;
}

declare global {
    interface Window {
        CanisterClient: typeof CanisterClient;
        canisterClient: CanisterClientWithMeta;
        collectDeviceInventoryByIP?: (ipAddress: string) => Promise<Result<DeviceInventory, string>>;
    }
}

window.CanisterClient = CanisterClient;

// Create global instance with cache-busting verification
try {
    console.log('🚀 Attempting to create CanisterClient...');
    window.canisterClient = new CanisterClient() as CanisterClientWithMeta;
    console.log('✅ CanisterClient created successfully:', typeof window.canisterClient);
} catch (error: unknown) {
    console.error('❌ Failed to create CanisterClient:', error);

    // Fallback: Create a minimal stub
    window.canisterClient = {
        collectDeviceInventoryByIP: async (ip: string) => {
            throw new Error('CanisterClient initialization failed, but method called for ' + ip);
        },
        cacheVersion: '20250924-2-fallback'
    } as CanisterClientWithMeta;
}

// Cache-busting verification - force reload detection
window.canisterClient.cacheVersion = '20251018-real-data-v3';
console.log('✅ CanisterClient loaded with cache version:', window.canisterClient.cacheVersion);
console.log('✅ Available methods:', Object.getOwnPropertyNames(CanisterClient.prototype).filter(name => name !== 'constructor'));

// Verify critical methods exist
if (typeof window.canisterClient.collectDeviceInventoryByIP === 'function') {
    console.log('✅ collectDeviceInventoryByIP method confirmed available');
} else {
    console.error('❌ collectDeviceInventoryByIP method missing - cache issue detected');

    // Emergency fallback: create the method directly on window object
    window.collectDeviceInventoryByIP = async function(ipAddress: string): Promise<Result<DeviceInventory, string>> {
        console.log('🚨 Using emergency fallback method for collectDeviceInventoryByIP');
        return await window.canisterClient.callCanister<Result<DeviceInventory, string>>(
            'aws_discovery_integration',
            'collect_device_inventory_by_ip',
            [ipAddress]
        );
    };

    // Also add to canisterClient if missing
    if (!window.canisterClient.collectDeviceInventoryByIP) {
        window.canisterClient.collectDeviceInventoryByIP = window.collectDeviceInventoryByIP;
        console.log('✅ Emergency fallback method added to canisterClient');
    }
}

// Export for use in other modules
